import "./TooltipsProvider.scss";

import { Tooltip } from "bootstrap";
import { ComponentOptions } from "bootstrap/js/dist/base-component";
import { createLogger } from "logging";
import React, { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";

import { TooltipsContext, TooltipsContextProps } from "./TooltipsContext";

const logger = createLogger("TooltipsProvider");

const template =
  '<div class="tooltip global-tooltip" role="tooltip">' +
  '<div class="tooltip-shadow"></div>' +
  '<div class="tooltip-inner"></div>' +
  '<div class="tooltip-arrow"></div>' +
  "</div>";

const preventClose = false;
const showDelay = 5;
const hideDelay = preventClose ? 100_000 : 10;

function hideGlobalTooltips() {
  let elements = document.getElementsByClassName("global-tooltip");

  for (const i in elements) {
    try {
      const element = elements[i];
      element.parentElement?.removeChild(element);
    } catch (ex) {
      logger.trace("hideTooltips(): failed to remove tooltip", ex);
    }
  }

  elements = document.getElementsByClassName("global-tooltip");
  if (elements.length) {
    logger.trace(`hideTooltips(): still ${elements.length} active tooltips found`);
    setTimeout(hideGlobalTooltips, 50);
  }
}

function getTitle(...args: unknown[]) {
  const el = args[0] as HTMLUnknownElement;
  let title = el.attributes.getNamedItem("title")?.value || el.attributes.getNamedItem("bs-title")?.value;
  if (!title) {
    const contentId = el.attributes.getNamedItem("data-for")?.value;
    if (contentId) {
      title = document.getElementById(contentId)?.innerHTML;
    }
  }

  logger.trace("getTitle(): ", title);
  return title;
}

export const TooltipsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();

  const hideTooltips = useCallback(() => {
    hideGlobalTooltips();
  }, []);

  const showTooltips = useCallback(
    (selector = "global-tooltip-container") => {
      const parent = document.getElementsByClassName(selector)[0];
      if (!parent) {
        logger.trace(`showTooltips(): parent not found for selector '${selector}'`);
        return;
      }

      const tooltipOptions: ComponentOptions = {
        selector: ".global-tooltip-trigger",
        trigger: "hover focus",
        container: "body",
        customClass: "global-tooltip",
        html: true,
        template,
        placement: "top",
        delay: { show: showDelay, hide: hideDelay },
        title: (...args: unknown[]) => {
          hideTooltips();
          return getTitle(...args);
        },
      };

      logger.trace("showTooltips(): configure bootstrap tooltip", selector);

      try {
        Tooltip.getOrCreateInstance(parent, tooltipOptions);
      } catch {
        // ignore
      }
    },
    [hideTooltips]
  );

  const showTooltip = useCallback((element: HTMLElement) => {
    logger.trace("showTooltip(): ", element);

    try {
      const widget = Tooltip.getInstance(element);
      widget?.show();
    } catch {
      // ignore
    }
  }, []);

  const hideTooltip = useCallback((element: HTMLElement) => {
    logger.trace("hideTooltip(): ", element);

    try {
      if (preventClose) {
        return;
      }
      const widget = Tooltip.getInstance(element);
      widget?.hide();
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    showTooltips();

    const parent = document.getElementsByClassName("global-tooltip-container")[0];
    if (!parent) {
      return;
    }

    const onMouseDown = () => hideTooltips();
    const onContextMenu = () => hideTooltips();

    parent.addEventListener("mousedown", onMouseDown);
    parent.addEventListener("contextmenu", onContextMenu);

    return () => {
      parent.removeEventListener("mousedown", onMouseDown);
      parent.removeEventListener("contextmenu", onContextMenu);
      hideTooltips();
    };
  }, [hideTooltips, showTooltips]);

  useEffect(() => {
    hideTooltips();
  }, [hideTooltips, location]);

  const value = useMemo<TooltipsContextProps>(
    () => ({
      showTooltips,
      showTooltip,
      hideTooltip,
      hideTooltips,
    }),
    [showTooltips, showTooltip, hideTooltip, hideTooltips]
  );

  return <TooltipsContext.Provider value={value}>{children}</TooltipsContext.Provider>;
};
